import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, type OperationAccountDto } from '@/api/client';

/**
 * 当前「经营账号」上下文
 *
 * ── 它解决什么问题 ──
 *
 * 企业空间（租户）里可以有 **多个经营账号**（P0-工程契约 §1 / A1 决议）：
 * 同一个老板既做「康美招生」又做「本地生活探店」，两边的定位、栏目、
 * 周计划、平台账号**各自独立**。
 *
 * 所以界面必须一直知道"我现在在哪个经营账号里"——
 * 否则会出现本仓库反复踩过的那类错：**上一个账号的选择残留下来，
 * 冒充当前账号的上下文**（auth store 的 `syncBusiness` 就是为这个存在的）。
 *
 * ── 三条纪律（照抄 auth.syncBusiness 的成熟做法）──
 *
 *   1. `load(mode='reset')`：**换号/登录成功瞬间**——先清空再按新账号的列表设置。
 *      这是唯一能确定"新身份是谁"的时刻。
 *   2. `load(mode='keep-if-owned')`：**刷新 bootstrap**——用户手动切过的账号，
 *      只有在**服务端列表里确实存在**时才保留；不在就重置。
 *      localStorage 里存的是 id，"它是不是本账号的"必须问服务端。
 *   3. 拉取失败**什么都不碰**。"拿不到列表" ≠ "没有账号"，
 *      网络抖一下就把用户选好的账号清掉是新的 bug。
 *
 * ── 与 `gfc_business_id` 的区别 ──
 *
 * `businessId` 是**生意档案**（描述"卖什么"），`accountId` 是**经营账号**
 * （描述"面向哪类客户做哪个号"）。P1 阶段两者并存、互不覆盖；
 * 生意档案挂到经营账号下是 P2 的事（`BusinessProfile.accountId` 属后续扩展项）。
 */

const ID_KEY = 'gfc_account_id';
const NAME_KEY = 'gfc_account_name';

export const useOperationStore = defineStore('operation', () => {
  const accounts = ref<OperationAccountDto[]>([]);
  const currentId = ref<string | null>(localStorage.getItem(ID_KEY));
  const currentName = ref<string | null>(localStorage.getItem(NAME_KEY));
  const loading = ref(false);
  /** 列表**已经拉过一次**（用来区分"还没拉"和"确实一个账号都没有"） */
  const loaded = ref(false);

  const current = computed(() => accounts.value.find((a) => a.id === currentId.value) ?? null);
  const currentStatus = computed(() => current.value?.status ?? null);
  /** 没有任何经营账号 —— 界面要出"新建经营账号"的引导，而不是显示空白 */
  const isEmpty = computed(() => loaded.value && accounts.value.length === 0);

  function setCurrent(id: string | null, name: string | null) {
    currentId.value = id;
    currentName.value = name;
    if (id) localStorage.setItem(ID_KEY, id);
    else localStorage.removeItem(ID_KEY);
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  }

  /**
   * 拉列表并校正当前选择。
   *
   * @param mode 'reset' 换号瞬间（先清空）｜'keep-if-owned' 刷新时（属于自己才保留）
   */
  async function load(mode: 'reset' | 'keep-if-owned' = 'keep-if-owned'): Promise<void> {
    if (mode === 'reset') setCurrent(null, null);
    loading.value = true;
    let list: OperationAccountDto[] | null = null;
    try {
      const r = await api.operationAccounts();
      list = r.items ?? [];
    } catch {
      // 拉取失败：不动当前选择（见纪律 3）
      list = null;
    } finally {
      loading.value = false;
    }
    if (!list) return;

    accounts.value = list;
    loaded.value = true;

    if (mode === 'keep-if-owned' && currentId.value) {
      const owned = list.find((a) => a.id === currentId.value);
      if (owned) {
        // 顺带纠正可能过期的名字
        setCurrent(owned.id, owned.name);
        return;
      }
    }

    setCurrent(null, null);
    // 默认选第一个**可用**的账号（archived 的不自动选）
    const first = list.find((a) => a.status !== 'archived') ?? null;
    if (first) setCurrent(first.id, first.name);
  }

  function select(id: string): void {
    const a = accounts.value.find((x) => x.id === id);
    if (!a) return;
    setCurrent(a.id, a.name);
  }

  function clear(): void {
    accounts.value = [];
    loaded.value = false;
    setCurrent(null, null);
  }

  return {
    accounts,
    currentId,
    currentName,
    current,
    currentStatus,
    loading,
    loaded,
    isEmpty,
    load,
    select,
    setCurrent,
    clear,
  };
});
